import { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import styled from 'styled-components';

import { UserRole } from '~/api/account/accountApiTypes';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { CommonLayout } from '~/atomic/layout/CommonLayout';
import { LayoutType } from '~/atomic/layout/DefaultLayout';
import { PageTemplate } from '~/atomic/organism/PageTemplate/PageTemplate';
import { UserProfile } from '~/atomic/page/user/UserProfile';
import { checkAuthToken } from '~/feature/authorization/checkAuthToken';
import { getUser } from '~/feature/user/user.data';
import { userSelector } from '~/feature/user/user.selector';
import {
  createStore, RootState, useAppSelector,
} from '~/store';

const UserPage: NextPage<{ errorCode?: number }> & LayoutType = () => {
  const { currentUser } = useAppSelector(userSelector);

  const title = `Пользователь ${currentUser?.name ?? ''} `
    + '| Произведения авторов полностью на Bookriver';

  const description = `Пользователь ${currentUser?.name ?? ''} `
    + '— литературно-издательская площадка Bookriver ⭐ Серии книг'
    + '⭐ Полные циклы произведений ⭐ Комментарии и отзывы читателей'
    + '⭐ Скачать произведения полностью в формате fb2 ✉'
    + 'Найдите интересную книгу на нашем сайте и читайте удобно онлайн';

  return (
    <PageTemplate
      isPublic={pagesConfigStore.user.isPublic}
      seo={{
        title,
        image: currentUser.photoUrl,
        description,
        type: 'profile',
        canonical: pagesConfigStore.user.as(currentUser?.username),
        customSeoTags: (
          <>
            {currentUser?.gender && (
              <meta property="profile:gender" content={currentUser?.gender} />
            )}
          </>
        ),
      }}
      content={(
        <SCUserPage>
          <UserProfile />
        </SCUserPage>
      )}
    />
  );
};

UserPage.getLayout = (page) => (
  <CommonLayout>{page}</CommonLayout>
);

export const getServerSideProps: GetServerSideProps<
{ state: RootState; },
{ username: string; }
> = async (context) => {
  const { store, dispatch } = await createStore();
  const isLoggedIn = await checkAuthToken(context, dispatch);

  const { username } = context.params;

  await dispatch(getUser(username));

  const { user: { currentUser } } = store.getState();

  if (!currentUser?.id) {
    return { notFound: true };
  }

  if (currentUser.role === UserRole.author) {
    return {
      redirect: {
        destination: pagesConfigStore.author.as(currentUser.username),
        permanent: false,
      },
    };
  }

  const state = store.getState();
  return { props: { state } };
};

const SCUserPage = styled.div`
  margin-top: 40px;
  margin-bottom: 60px;
`;

export default UserPage;
